import {Component, Input, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {CommonService} from '../../../../shared/services/common.service';
import {MetricOptionModel} from '../../../../../models/metric-option.model';
import {Store} from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';
import {
  AddMetricValueRequest,
  CheckMetricsValueExistsByDateRequest,
  EditMetricValueListRequest
} from '../../core/metrics.actions';
import * as moment from 'moment';
import * as Validations from '../../../../shared/constants/validation.constants';
import {measurements} from '../../../../shared/constants/common.constants';
import {Subject, takeUntil} from 'rxjs';
import {selectedUserProfile} from '../../../../shared/core/shared.selectors';
import {MetricsService} from '../../core/metrics.service';
import {Metrics} from '../../services/metrics';

@Component({
  selector: 'app-enter-data',
  templateUrl: './enter-data.component.html',
  styleUrls: ['./enter-data.component.css']
})
export class EnterDataComponent {
  private readonly onDestroy: Subject<any> = new Subject<any>();
  @Input() data: any;
  maxDate = moment();

  oldDataExists = false;

  validations = Validations;
  metricOptions: MetricOptionModel[] = [];
  errorMessages: any = {};
  errorMessageMap = {
    recordedOn: {
      required: 'This field is required.',
      matDatepickerMax: 'Future date is not allowed.'
    },
    height: {
      min: 'Height is out of accepted range.',
      max: 'Height is out of accepted range.'
    },
    weight: {
      min: 'Weight is out of accepted range.',
      max: 'Weight is out of accepted range.'
    },
    bp: {
      pattern: 'Invalid value.',
      invalid: 'Systolic or diastolic value must not be zero or empty.'
    }
  }
  form = new FormGroup<any>({
    recordedOn: new FormControl('', [Validators.required])
  })

  constants = measurements;

  constructor(
    private commonService: CommonService,
    private store: Store<fromRoot.State>,
    private metricsService: Metrics
  ) {
  }

  ngOnInit() {
    this.setDateWatcher();

    if (this.data['defaultDate']) {
      this.form.controls['recordedOn'].setValue(this.data['defaultDate']);
    }

    this.createForm();

    this.metricsService.updateMetricsValues
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.oldDataExists = !!data?.length;

        if (this.form) {

          if (this.oldDataExists) {
            const values = {};
            data.forEach((o) => {
              values[o.metric.code] = o.value;
            });

            this.form.patchValue(values);
          } else {
            const recordedOn = this.form.controls['recordedOn'].value;
            this.form = new FormGroup({
              recordedOn: new FormControl('', [Validators.required])
            });
            this.createForm();
            this.form.patchValue({recordedOn}, {emitEvent: false});
            this.setDateWatcher();
          }
        }
      });
  }

  createForm() {
    this.metricOptions = this.data['metricsOptions'] || [];

    this.metricOptions.forEach(option => {
      const control = new FormControl('');
      this.form.addControl(option.code, control);

      // if option is bp then add bp regex validator and valid bp validator
      if (option.code === this.constants.metricOption.bp) {
        control.addValidators([
          Validators.pattern(this.validations.measurements.bp.regex),
          this.customBPValidator.bind(this)
        ])
      }
      // if option is height then add min height validator
      if (option.code === this.constants.metricOption.height) {
        control.addValidators([
          Validators.min(this.validations.measurements.height.min),
          Validators.max(this.validations.measurements.height.max)
        ])
      }
      // if option is weight then add min weight validator
      if (option.code === this.constants.metricOption.weight) {
        control.addValidators([
          Validators.min(this.validations.measurements.weight.min),
          Validators.max(this.validations.measurements.weight.max)
        ])
      }
    });
  }

  setDateWatcher() {
    this.form.controls['recordedOn'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((date) => {
        date = moment(date).format('YYYY-MM-DD');
        let userId;

        this.store.select(selectedUserProfile)
          .subscribe((profileData: any) => {
            userId = profileData?.profile?.id;
          }).unsubscribe();

        this.store.dispatch(CheckMetricsValueExistsByDateRequest({
          payload: {
            date,
            ...(userId ? {userId} : {})
          }
        }))
      });
  }

  handleSubmit() {
    if (this.form.invalid) {
      this.errorMessages = this.commonService.checkFormValidation(this.form, this.errorMessageMap);

      // check for error field and focus
      for (const controlsKey in this.form.controls) {
        const control = this.form.controls[controlsKey];
        if (control.invalid) {
          if (controlsKey === measurements.metricOption.height) {
            document.getElementById('measurement-height-input').scrollIntoView();
          } else if (controlsKey === measurements.metricOption.bp) {
            document.getElementById('measurement-bp-input').scrollIntoView();
          } else {
            const parentElement = document.getElementById('measurement-value-entry');
            const invalidControl: HTMLInputElement = parentElement.querySelector('[ng-reflect-name="' + controlsKey + '"]')
            invalidControl?.focus();
          }
        }
      }
      return;
    }

    const rawValue: any = this.form.getRawValue();
    // collect all values
    const values = [];

    for (const code in rawValue) {
      const metricOption = this.metricOptions.find((item) => item.code === code);
      const value = rawValue[code]?.toString().trim();
      if (metricOption && value) {
        values.push({
          metricId: metricOption.id,
          value: value.toString()
        })
      }
    }


    // check at least one value is filled
    if (!values.length) {
      this.commonService.notification('At least one of metric option value must be entered.', 'danger');
      return;
    }

    const payload = {
      recordedOn: moment(rawValue.recordedOn).format('YYYY-MM-DD'),
      values
    };

    if (this.data['memberId']) {
      payload['userId'] = this.data['memberId'];
    }

    if (this.data['groupId']) {
      payload['groupId'] = this.data['groupId'];
    }

    this.store.dispatch(AddMetricValueRequest({
      payload
    }))
  }

  handleCancel() {
    this.commonService.closeDialog();
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  getDirtyValues(form: any) {
    let dirtyValues = {};

    Object.keys(form.controls)
      .forEach(key => {
        let currentControl = form.controls[key];

        if (currentControl.dirty) {
          if (currentControl.controls)
            dirtyValues[key] = this.getDirtyValues(currentControl);
          else
            dirtyValues[key] = currentControl.value;
        }
      });

    return dirtyValues;
  }

  customBPValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value) {
      const valueParts = control.value.split('/');
      if (valueParts.length !== 2) {
        return null;
      }
      const [systolic, diastolic] = valueParts;
      if (!(+systolic) || !(+diastolic)) { // one of the value is 0 then
        return {invalid: true};
      }
    }
    return null;
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
